import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const QuickMatchCreator = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [quickMatch, setQuickMatch] = useState({
    title: '',
    date: '',
    time: '',
    venue: '',
    maxPlayers: 12,
    pricePerPlayer: 25
  });
  const navigate = useNavigate();

  const venueOptions = [
    { value: 'campo-morumbi', label: 'Campo do Morumbi' },
    { value: 'quadra-vila', label: 'Quadra da Vila' },
    { value: 'arena-central', label: 'Arena Central' },
    { value: 'campo-jardins', label: 'Campo dos Jardins' }
  ];

  const playerOptions = [
    { value: 8, label: '8 jogadores (4x4)' },
    { value: 10, label: '10 jogadores (5x5)' },
    { value: 12, label: '12 jogadores (6x6)' },
    { value: 14, label: '14 jogadores (7x7)' }
  ];

  const handleInputChange = (field, value) => {
    setQuickMatch(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleQuickCreate = () => {
    if (!quickMatch?.title || !quickMatch?.date || !quickMatch?.time || !quickMatch?.venue) {
      alert('Preencha todos os campos obrigatórios');
      return;
    }
    
    // Navigate to match details with pre-filled data
    navigate('/match-details', { 
      state: { 
        quickMatch: quickMatch,
        mode: 'create'
      } 
    });
  };

  const handleFullCreate = () => {
    navigate('/match-details');
  };

  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow?.setDate(tomorrow?.getDate() + 1);
    return tomorrow?.toISOString()?.split('T')?.[0];
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })?.format(amount);
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-2 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
              <Icon name="Plus" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-lg text-card-foreground">
                Criar Jogo
              </h2>
              <p className="text-sm text-muted-foreground">
                Organize uma nova partida rapidamente
              </p>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            iconPosition="right"
            iconSize={16}
          >
            {isExpanded ? 'Menos' : 'Mais'}
          </Button>
        </div>
      </div>
      {/* Quick Actions */}
      <div className="p-4">
        {!isExpanded ? (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Button
                variant="default"
                onClick={handleFullCreate}
                iconName="Calendar"
                iconPosition="left"
                iconSize={16}
                className="justify-start"
              >
                <div className="text-left">
                  <div className="font-medium">Jogo Completo</div>
                  <div className="text-xs opacity-80">Configurações avançadas</div>
                </div>
              </Button>
              
              <Button
                variant="outline"
                onClick={() => navigate('/venue-search')}
                iconName="MapPin"
                iconPosition="left"
                iconSize={16}
                className="justify-start"
              >
                <div className="text-left">
                  <div className="font-medium">Buscar Quadra</div>
                  <div className="text-xs opacity-80">Encontrar locais disponíveis</div>
                </div>
              </Button>
            </div>

            <Button
              variant="secondary"
              fullWidth
              onClick={() => setIsExpanded(true)}
              iconName="Zap"
              iconPosition="left"
              iconSize={16}
            >
              Criação Rápida
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Quick Form */}
            <div className="space-y-3">
              <Input
                label="Nome do Jogo"
                type="text"
                placeholder="Ex: Jogo de Sábado"
                value={quickMatch?.title}
                onChange={(e) => handleInputChange('title', e?.target?.value)}
                required
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  label="Data"
                  type="date"
                  value={quickMatch?.date}
                  onChange={(e) => handleInputChange('date', e?.target?.value)}
                  min={getTomorrowDate()}
                  required
                />
                
                <Input
                  label="Horário"
                  type="time"
                  value={quickMatch?.time}
                  onChange={(e) => handleInputChange('time', e?.target?.value)}
                  required
                />
              </div>

              <Select
                label="Local"
                placeholder="Escolha uma quadra"
                options={venueOptions}
                value={quickMatch?.venue}
                onChange={(value) => handleInputChange('venue', value)}
                required
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Select
                  label="Número de Jogadores"
                  options={playerOptions}
                  value={quickMatch?.maxPlayers}
                  onChange={(value) => handleInputChange('maxPlayers', value)}
                />
                
                <Input
                  label="Valor por Jogador"
                  type="number"
                  placeholder="25"
                  value={quickMatch?.pricePerPlayer}
                  onChange={(e) => handleInputChange('pricePerPlayer', parseFloat(e?.target?.value) || 0)}
                  min="0"
                  step="5"
                />
              </div>
            </div>

            {/* Summary */}
            {quickMatch?.title && quickMatch?.date && quickMatch?.time && (
              <div className="bg-muted/50 rounded-lg p-3">
                <h4 className="font-medium text-card-foreground mb-2">Resumo</h4>
                <div className="space-y-1 text-sm text-muted-foreground">
                  <div className="flex items-center justify-between">
                    <span>Total estimado:</span>
                    <span className="font-data font-medium text-card-foreground">
                      {formatCurrency(quickMatch?.maxPlayers * quickMatch?.pricePerPlayer)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Por jogador:</span>
                    <span className="font-data font-medium text-card-foreground">
                      {formatCurrency(quickMatch?.pricePerPlayer)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex space-x-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsExpanded(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleQuickCreate}
                iconName="ArrowRight"
                iconPosition="right"
                iconSize={16}
                className="flex-1"
                disabled={!quickMatch?.title || !quickMatch?.date || !quickMatch?.time || !quickMatch?.venue}
              >
                Continuar
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuickMatchCreator;